/**
 * RunTerminal component handles the execution of student code with custom inputs
 * and displays the compilation and runtime results in a terminal-like interface.
 */
import log from 'loglevel';

import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as constants from 'Constants';
import httpService from 'services/httpService.js';
import { Modal } from "react-bootstrap";
// The backend labels the failure in message/verdict; without this the terminal renders raw stderr, or nothing at all.
const formatRunResult = (runResult) => {
  const compilationError = runResult.compilation_errors || runResult.error;
  if (compilationError) {
    return `Compilation Error\n================\n${compilationError}`;
  }
  const failed =
    runResult.status === 'fail' ||
    Boolean(runResult.runtime_errors) ||
    Boolean(runResult.return_code);
  if (failed) {
    const label = runResult.verdict || runResult.message || 'Runtime Error';
    const detail =
      runResult.runtime_errors ||
      'Your program did not run to completion, so there is no output to show.';
    return `${label}\n================\n${detail}`;
  }
  return runResult.output || '(no output)';
};

// RunTerminal component definition
const RunTerminal = ({
  errorOnRun,
  programId,
  student_id,
  language,
  runToggle,
  successOnRun,
  setRunToggle,
  setErrorOnRun,
  setSuccessOnRun,
  isRunLoading,
  toggle,
  setToggle
}) => {
  // State variables for the component
  const [compilationMessage, setCompilationMessage] = useState("");
  const [userInput, setUserInput] = useState("");
  const [showTerminal, setShowTerminal] = useState(false);
  const [result, setResult] = useState("");
  const [showMaxRunsModal, setShowMaxRunsModal] = useState(false);

  //State to check if the user is in the output screen
  const [isInOutputScreen, setIsInOutputScreen] = useState(false);

  // Constants for compilation messages
  const compilationFailed = ' Compilation Failed\n================\n';
  const compilationSuccesfull =
    'Compilation Successful\nEnter Inputs Below\n==================\n';

  // useEffect hook to set compilation message based on the success or failure of the code execution
  useEffect(() => {
    if (errorOnRun) setCompilationMessage(compilationFailed + errorOnRun);
    if (successOnRun) setCompilationMessage(compilationSuccesfull);
  }, [errorOnRun, successOnRun]);

  // useEffect hook to handle changes in runToggle state
  useEffect(() => {
    if (runToggle) setShowTerminal(true);

    setUserInput('');
    setIsInOutputScreen(false);
    setResult('');
    if (successOnRun) setCompilationMessage(compilationSuccesfull);
  }, [runToggle]);

  // Function to handle changes in the input text. It checks if the input text starts with the compilation message, and prevents the
  // user from editing the content of the terminal.
  const handleChange = (e) => {
    const newText = e.target.value;
    if (newText.startsWith(compilationMessage)) {
      setUserInput(newText.slice(compilationMessage.length));
    }
  };

  // Function to handle submission of the input text
  const handleInputSubmit = async () => {
    try {
      const url =
        constants.base_url + "/evalpro/editor/run_custom_input/" + programId;
      const form_data = new FormData();
      //let studentEmail = submission?.email ? submission.email : "";
      //form_data.append("Student_Email", studentEmail);
      form_data.append("programming_language", language);
      form_data.append("input_text", userInput);
      form_data.append("key", "buffer_data");
      const response = await httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

      const runResult = response.data.result || {};
      setResult(formatRunResult(runResult));
      setCompilationMessage("");
      setUserInput(""); // Clear user input after successful submission
      setIsInOutputScreen(true);
      setToggle(!toggle);
    } catch (error) {
      log.error("run custom input error: ", error);
      if (error?.response?.status === 403) {
        setShowMaxRunsModal(true);
        return;
      }
      // Without this the terminal stays on "Compiling..." forever when the run fails server-side.
      setResult(
        "Run Failed\n================\nYour program could not be run. Please try again."
      );
      setCompilationMessage("");
      setUserInput("");
      setIsInOutputScreen(true);
    };
  };

  const handleRunAgain = () => {
    setResult('');
    setUserInput('');
    setIsInOutputScreen(false);
    if (successOnRun) setCompilationMessage(compilationSuccesfull);
  };

  const handleCloseTerminal = () => {
    setUserInput("");
    setShowTerminal(false);
    setRunToggle(false);
    setSuccessOnRun(false);
    setErrorOnRun("");
    setResult("");
    setIsInOutputScreen(false);
  };


  // Render the component
  return (
    showTerminal && (
      <div style={{ position: "relative" }}>
        <textarea
          value={
            isRunLoading
              ? "Compiling..."
              : result + compilationMessage + userInput
          }
          onChange={handleChange}
          style={{
            color: "white",
            backgroundColor: "black",
            width: "100%",
            height: "200px",
            minHeight: "120px",
            scrollbarWidth: "none",
          }}
        />
        <OverlayTrigger overlay={<Tooltip>Close Terminal</Tooltip>}>
          <Button
            variant="secondary"
            style={{
              position: "absolute",
              top: "5px",
              right: "10px",
              color: "white",
              padding: "2px",
            }}
            onClick={handleCloseTerminal}
          >
            <IoMdCloseCircleOutline />
          </Button>
        </OverlayTrigger>
        <Modal show={showMaxRunsModal} centered>
          <Modal.Header closeButton={false}>
            <Modal.Title>Maximum Run Attempts Reached</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            You have reached the maximum number of run attempts without submitting.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMaxRunsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
        {isInOutputScreen ? (
          <Button
            variant="success"
            style={{ position: "absolute", bottom: "10px", right: "10px" }}
            onClick={handleRunAgain}
          >
            Run Again
          </Button>
        ) : (
          <Button
            variant="primary"
            style={{ position: "absolute", bottom: "10px", right: "10px" }}
            onClick={handleInputSubmit}
            disabled={!!errorOnRun}
          >
            Send Inputs
          </Button>
        )}
      </div>
    )
  );
};

// Exporting the RunTerminal component
export default RunTerminal;
